import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import NavbarComponent from "./Nav";
import { backendURL } from "./IPaddress";
import { useAuth } from "./store/AuthContext";
import { useSchoolYear } from "./store/SchoolYearCalculator";

const OBC_METRIC_SECTIONS = [
  {
    key: "obcWorkshop",
    title: "A. OBC Workshop Totals",
    endpoint: "obc_counts",
    downloadEndpoint: "download_A_OBC_workshop_report",
    downloadName: "A_OBC_workshop_report",
    buttonLabel: "Download A. OBC Workshop Report",
  },
  {
    key: "obcClassroom",
    title: "B. OBC Classroom Implementation Totals",
    endpoint: "obc_report_b",
    downloadEndpoint: "download_B_obc_classroom_implementation_report",
    downloadName: "B_obc_classroom_implementation_report",
    buttonLabel: "Download B. OBC Classroom Implementation Report",
  },
  {
    key: "obcPerformance",
    title: "C. OBC Performance Totals",
    endpoint: "obc_report_c",
    downloadEndpoint: "download_C_OBC_performance_report",
    downloadName: "C_OBC_performance_report",
    buttonLabel: "Download C. OBC Performance Report",
  },
];

const UHSMTA_SECTIONS = [
  {
    key: "adjudication",
    title: "A. Adjudication Feedback",
    downloadEndpoint: "download_UHSMTA_adjudication_feedback_report",
    downloadName: "A_UHSMTA_adjudication_feedback_report",
  },
  {
    key: "coaching",
    title: "B. Group & One-on-One Coaching",
    downloadEndpoint: "download_B_UHSMTA_group_or_one_on_one_report",
    downloadName: "B_UHSMTA_group_or_one_on_one_report",
  },
  {
    key: "workshops",
    title: "C. Workshops",
    downloadEndpoint: "download_C_UHSMTA_workshops_report",
    downloadName: "C_UHSMTA_workshops_report",
  },
  {
    key: "award_shows",
    title: "D. Award Shows",
    downloadEndpoint: "download_D_UHSMTA_award_shows_report",
    downloadName: "D_UHSMTA_award_shows_report",
  },
  {
    key: "winners",
    title: "E. Winners",
    downloadEndpoint: "download_E_UHSMTA_winners_report",
    downloadName: "E_UHSMTA_winners_report",
  },
  {
    key: "interns",
    title: "F. Interns",
    downloadEndpoint: "download_F_UHSMTA_interns_report",
    downloadName: "F_UHSMTA_interns_report",
  },
  {
    key: "special_performance",
    title: "G. Special Performances",
    downloadEndpoint: "download_G_UHSMTA_special_performance_report",
    downloadName: "G_UHSMTA_special_performance_report",
  },
];

// Per-section reports (OBC A-C, Attendance, TYP, UHSMTA A-G) download from
// their totals cards above; only reports without a card live here.
const REPORT_GROUPS = [
  {
    title: "OBC Reports",
    reports: [
      { endpoint: "download_obc_mentor_teacher_assignments", name: "OBC_mentor_teacher_assignments", label: "Download OBC Mentor & Teacher Assignments" },
    ],
  },
  {
    title: "Full Data & Total Reports",
    reports: [
      { endpoint: "download_get_full_data_report", name: "get_full_data_report", label: "Download Full Data Report" },
      { endpoint: "download_get_total_schools_report", name: "get_total_schools_report", label: "Download Total Schools Report" },
      { endpoint: "download_students_teachers_per_school_report", name: "students_teachers_per_school_report", label: "Download Students & Teachers per School Report" },
      { endpoint: "download_full_ticket_forms_request_list", name: "full_ticket_forms_request_list", label: "Download Full Ticket Forms Request Report", withDateRange: true },
      { endpoint: "download_dashboard_totals_report", name: "dashboard_totals_report", label: "Download Dashboard Totals Report" },
    ],
  },
];

const fmtInt = (n) => Number(n ?? 0).toLocaleString();
const fmtHours = (h) => Number(h ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

function Reporting() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const { currentYear, schoolYearListParent } = useSchoolYear();

  const [schoolYearList, setSchoolYearList] = useState([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState("");
  const [obcMetrics, setObcMetrics] = useState({});
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsError, setCountsError] = useState(null);
  const [typCounts, setTypCounts] = useState(null);
  const [typLoading, setTypLoading] = useState(false);
  const [typError, setTypError] = useState(null);
  const [attendanceCounts, setAttendanceCounts] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState(null);
  const [uhsmtaCounts, setUhsmtaCounts] = useState(null);
  const [uhsmtaLoading, setUhsmtaLoading] = useState(false);
  const [uhsmtaError, setUhsmtaError] = useState(null);
  const [downloadNotice, setDownloadNotice] = useState(null);
  // Optional performance-date range for the Full Ticket Forms Request report;
  // when either bound is set it replaces the school-year scoping.
  const [ticketRangeStart, setTicketRangeStart] = useState("");
  const [ticketRangeEnd, setTicketRangeEnd] = useState("");

  useEffect(() => {
    if (!authState) navigate("/login");
  }, [authState, navigate]);

  useEffect(() => {
    if (currentYear && schoolYearListParent.length) {
      setSelectedSchoolYear(String(currentYear));
      setSchoolYearList(schoolYearListParent);
    }
  }, [currentYear, schoolYearListParent]);

  const schoolYearName = useMemo(() => {
    const yearObj = schoolYearList.find((year) => year.id === parseInt(selectedSchoolYear, 10));
    return yearObj?.school_year ?? "Unknown Year";
  }, [schoolYearList, selectedSchoolYear]);

  useEffect(() => {
    if (!selectedSchoolYear) return;

    const ac = new AbortController();
    const run = async () => {
      setCountsLoading(true);
      setCountsError(null);
      try {
        const results = await Promise.all(
          OBC_METRIC_SECTIONS.map(async (section) => {
            const res = await fetch(`${backendURL}/api/${section.endpoint}/${encodeURIComponent(selectedSchoolYear)}`, {
              credentials: "include",
              signal: ac.signal,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            return [section.key, data];
          })
        );
        setObcMetrics(Object.fromEntries(results));
      } catch (err) {
        if (err.name !== "AbortError") {
          setCountsError(err.message || "Failed to load report totals");
        }
      } finally {
        setCountsLoading(false);
      }
    };

    run();
    return () => ac.abort();
  }, [selectedSchoolYear]);

  useEffect(() => {
    if (!selectedSchoolYear) return;

    const ac = new AbortController();
    const run = async () => {
      setTypLoading(true);
      setTypError(null);
      try {
        const res = await fetch(`${backendURL}/api/typ_counts/${encodeURIComponent(selectedSchoolYear)}`, {
          credentials: "include",
          signal: ac.signal,
        });
        if (res.status === 404) {
          setTypCounts(null);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setTypCounts(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          setTypError(err.message || "Failed to load Theater for Young People totals");
        }
      } finally {
        setTypLoading(false);
      }
    };

    run();
    return () => ac.abort();
  }, [selectedSchoolYear]);

  useEffect(() => {
    if (!selectedSchoolYear) return;

    const ac = new AbortController();
    const run = async () => {
      setAttendanceLoading(true);
      setAttendanceError(null);
      try {
        const res = await fetch(`${backendURL}/api/attendance_counts/${encodeURIComponent(selectedSchoolYear)}`, {
          credentials: "include",
          signal: ac.signal,
        });
        if (res.status === 404) {
          setAttendanceCounts(null);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setAttendanceCounts(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          setAttendanceError(err.message || "Failed to load attendance totals");
        }
      } finally {
        setAttendanceLoading(false);
      }
    };

    run();
    return () => ac.abort();
  }, [selectedSchoolYear]);

  useEffect(() => {
    if (!selectedSchoolYear) return;

    const ac = new AbortController();
    const run = async () => {
      setUhsmtaLoading(true);
      setUhsmtaError(null);
      try {
        const res = await fetch(`${backendURL}/api/uhsmta_counts/${encodeURIComponent(selectedSchoolYear)}`, {
          credentials: "include",
          signal: ac.signal,
        });
        if (res.status === 404) {
          setUhsmtaCounts(null);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setUhsmtaCounts(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          setUhsmtaError(err.message || "Failed to load UHSMTA totals");
        }
      } finally {
        setUhsmtaLoading(false);
      }
    };

    run();
    return () => ac.abort();
  }, [selectedSchoolYear]);

  const downloadReport = async (endpoint, reportName, { query = "", nameSuffix } = {}) => {
    if (!selectedSchoolYear) return;
    setDownloadNotice(null);
    try {
      const response = await fetch(`${backendURL}/api/${endpoint}/${selectedSchoolYear}${query}`, {
        method: "GET",
        credentials: "include",
      });
      if (response.status === 404) {
        setDownloadNotice(`No data available to download for this report (${nameSuffix ?? schoolYearName}).`);
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${reportName}_${nameSuffix ?? schoolYearName}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      // keep lightweight failure handling for now; can be replaced with modal/toast later
      console.error(`Failed to download ${reportName}:`, error);
    }
  };

  const getMetricItems = (counts) => [
    { label: "Events", value: fmtInt(counts?.number_of_events) },
    { label: "Unique Districts", value: fmtInt(counts?.number_of_unique_districts) },
    { label: "Charter Schools", value: fmtInt(counts?.number_of_charters) },
    { label: "Schools (Non-Charter)", value: fmtInt(counts?.number_of_schools) },
    { label: "Total Hours", value: fmtHours(counts?.total_hours), hint: "Hours" },
    { label: "Teachers", value: fmtInt(counts?.number_of_teachers) },
    { label: "Students", value: fmtInt(counts?.total_number_of_students) },
    // Only Section C (OBC Performance) returns total_student_body; A & B omit it (null) so it is hidden there.
    ...(counts?.total_student_body != null
      ? [{ label: "Total Student Body", value: fmtInt(counts.total_student_body) }]
      : []),
  ];

  const getAttendanceItems = (counts) => [
    { label: "Events", value: fmtInt(counts?.number_of_events) },
    {
      label: "Dates with Tickets",
      value: `${fmtInt(counts?.event_dates_with_tickets)} of ${fmtInt(counts?.total_event_dates)}`,
    },
    { label: "Total Hours", value: fmtHours(counts?.total_hours), hint: "Hours" },
    { label: "Unique Districts", value: fmtInt(counts?.number_of_unique_districts) },
    { label: "Charter Schools", value: fmtInt(counts?.number_of_charters) },
    { label: "Schools (Non-Charter)", value: fmtInt(counts?.number_of_schools) },
    { label: "Teachers", value: fmtInt(counts?.number_of_teachers) },
    { label: "Students", value: fmtInt(counts?.number_of_students) },
  ];

  // Metrics are skipped when the payload omits them or returns null (N/A for that section).
  const getUhsmtaItems = (counts) => {
    const metrics = [
      { key: "number_of_events", label: "Events", fmt: fmtInt },
      { key: "total_hours", label: "Total Hours", fmt: fmtHours, hint: "Hours" },
      { key: "number_of_unique_districts", label: "Unique Districts", fmt: fmtInt },
      { key: "number_of_charters", label: "Charter Schools", fmt: fmtInt },
      { key: "number_of_schools", label: "Schools (Non-Charter)", fmt: fmtInt },
      { key: "number_of_teachers", label: "Teachers", fmt: fmtInt },
      { key: "number_of_students", label: "Students", fmt: fmtInt },
    ];
    return metrics
      .filter((metric) => counts?.[metric.key] != null)
      .map((metric) => ({ label: metric.label, value: metric.fmt(counts[metric.key]), hint: metric.hint }));
  };

  const getTypItems = (counts) => [
    { label: "Events", value: fmtInt(counts?.number_of_events) },
    { label: "Total Hours", value: fmtHours(counts?.total_hours), hint: "Hours" },
    { label: "Unique Districts", value: fmtInt(counts?.number_of_unique_districts) },
    { label: "Charter Schools", value: fmtInt(counts?.number_of_charters) },
    { label: "Schools (Non-Charter)", value: fmtInt(counts?.number_of_schools) },
    { label: "Students", value: fmtInt(counts?.number_of_students) },
    { label: "Teachers", value: fmtInt(counts?.number_of_teachers) },
  ];

  return (
    <div>
      {authState && <NavbarComponent />}
      <Container fluid className="py-3">
        <Row className="align-items-end g-3 mb-3">
          <Col xs={12} md={6}>
            <h1 className="mb-1">Reports</h1>
            <div className="text-muted">Export reports and review OBC totals by school year.</div>
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Form.Group controlId="schoolYear">
              <Form.Label className="fw-bold">Select School Year</Form.Label>
              <Form.Select value={selectedSchoolYear} onChange={(event) => setSelectedSchoolYear(event.target.value)}>
                <option value="" disabled>Select School Year</option>
                {schoolYearList.map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.school_year}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {downloadNotice && (
          <Alert variant="info" dismissible onClose={() => setDownloadNotice(null)}>
            {downloadNotice}
          </Alert>
        )}

        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h2 className="h5 mb-0">OBC Summary Totals</h2>
              {selectedSchoolYear && (
                <Badge bg="secondary">School Year: {schoolYearName}</Badge>
              )}
            </div>

            {countsLoading && (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" role="status" />
              </div>
            )}

            {countsError && !countsLoading && (
              <Alert variant="danger" className="mb-0">
                Error loading totals: {countsError}
              </Alert>
            )}

            {!countsLoading && !countsError && (
              <Row className="g-3">
                {OBC_METRIC_SECTIONS.map((section) => {
                  const items = getMetricItems(obcMetrics[section.key]);
                  return (
                    <Col key={section.key} xs={12} xl={4}>
                      <Card className="h-100">
                        <Card.Body>
                          <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
                            <h3 className="h6 mb-0">{section.title}</h3>
                            <Button size="sm" onClick={() => downloadReport(section.downloadEndpoint, section.downloadName)}>
                              Download
                            </Button>
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            {items.map((item) => (
                              <Card key={item.label} className="shadow-sm flex-shrink-0" style={{ width: "9.75rem" }}>
                                <Card.Body className="py-2 text-center">
                                  <div className="fw-bold fs-5 lh-1">{item.value}</div>
                                  <div className="text-muted small lh-sm">{item.label}</div>
                                  {item.hint && <Badge bg="secondary" className="mt-1">{item.hint}</Badge>}
                                </Card.Body>
                              </Card>
                            ))}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h2 className="h5 mb-0">Attendance Totals</h2>
              <Button
                size="sm"
                onClick={() => downloadReport("download_student_teacher_report", "A_student_teacher_in_attendance_report")}
              >
                Download
              </Button>
            </div>

            {attendanceLoading && (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" role="status" />
              </div>
            )}

            {attendanceError && !attendanceLoading && (
              <Alert variant="danger" className="mb-0">
                Error loading totals: {attendanceError}
              </Alert>
            )}

            {!attendanceLoading && !attendanceError && (
              <div className="d-flex flex-wrap gap-2">
                {getAttendanceItems(attendanceCounts).map((item) => (
                  <Card key={item.label} className="shadow-sm flex-shrink-0" style={{ width: "9.75rem" }}>
                    <Card.Body className="py-2 text-center">
                      <div className="fw-bold fs-5 lh-1">{item.value}</div>
                      <div className="text-muted small lh-sm">{item.label}</div>
                      {item.hint && <Badge bg="secondary" className="mt-1">{item.hint}</Badge>}
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h2 className="h5 mb-0">Theater for Young People Totals</h2>
              <Button
                size="sm"
                onClick={() => downloadReport("download_typ_report", "theater_for_young_people_report")}
              >
                Download
              </Button>
            </div>

            {typLoading && (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" role="status" />
              </div>
            )}

            {typError && !typLoading && (
              <Alert variant="danger" className="mb-0">
                Error loading totals: {typError}
              </Alert>
            )}

            {!typLoading && !typError && (
              <div className="d-flex flex-wrap gap-2">
                {getTypItems(typCounts).map((item) => (
                  <Card key={item.label} className="shadow-sm flex-shrink-0" style={{ width: "9.75rem" }}>
                    <Card.Body className="py-2 text-center">
                      <div className="fw-bold fs-5 lh-1">{item.value}</div>
                      <div className="text-muted small lh-sm">{item.label}</div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h2 className="h5 mb-0">UHSMTA Totals</h2>
              {selectedSchoolYear && (
                <Badge bg="secondary">School Year: {schoolYearName}</Badge>
              )}
            </div>

            {uhsmtaLoading && (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" role="status" />
              </div>
            )}

            {uhsmtaError && !uhsmtaLoading && (
              <Alert variant="danger" className="mb-0">
                Error loading totals: {uhsmtaError}
              </Alert>
            )}

            {!uhsmtaLoading && !uhsmtaError && (
              <Row className="g-3">
                {UHSMTA_SECTIONS.map((section) => {
                  const items = getUhsmtaItems(uhsmtaCounts?.[section.key]);
                  return (
                    <Col key={section.key} xs={12} xl={4}>
                      <Card className="h-100">
                        <Card.Body>
                          <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
                            <h3 className="h6 mb-0">{section.title}</h3>
                            <Button size="sm" onClick={() => downloadReport(section.downloadEndpoint, section.downloadName)}>
                              Download
                            </Button>
                          </div>
                          {items.length === 0 ? (
                            <div className="text-muted small">No totals available.</div>
                          ) : (
                            <div className="d-flex flex-wrap gap-2">
                              {items.map((item) => (
                                <Card key={item.label} className="shadow-sm flex-shrink-0" style={{ width: "9.75rem" }}>
                                  <Card.Body className="py-2 text-center">
                                    <div className="fw-bold fs-5 lh-1">{item.value}</div>
                                    <div className="text-muted small lh-sm">{item.label}</div>
                                    {item.hint && <Badge bg="secondary" className="mt-1">{item.hint}</Badge>}
                                  </Card.Body>
                                </Card>
                              ))}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card.Body>
        </Card>

        <Row className="g-3">
          {REPORT_GROUPS.map((group) => (
            <Col key={group.title} xs={12} lg={6}>
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <h2 className="h5 mb-3">{group.title}</h2>
                  <div className="d-flex flex-column gap-2">
                    {group.reports.map((report) =>
                      report.withDateRange ? (
                        <div key={report.endpoint} className="border rounded p-3 bg-body-tertiary">
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                            <span className="fw-semibold">Full Ticket Forms Request Report</span>
                            {(ticketRangeStart || ticketRangeEnd) && (
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={() => {
                                  setTicketRangeStart("");
                                  setTicketRangeEnd("");
                                }}
                              >
                                Clear dates
                              </Button>
                            )}
                          </div>
                          <Row className="g-2 mb-2">
                            <Col xs={6}>
                              <Form.Group controlId="ticketRangeStart">
                                <Form.Label className="small text-muted mb-1">
                                  Performances from
                                </Form.Label>
                                <Form.Control
                                  type="date"
                                  size="sm"
                                  value={ticketRangeStart}
                                  onChange={(e) => setTicketRangeStart(e.target.value)}
                                />
                              </Form.Group>
                            </Col>
                            <Col xs={6}>
                              <Form.Group controlId="ticketRangeEnd">
                                <Form.Label className="small text-muted mb-1">
                                  Performances through
                                </Form.Label>
                                <Form.Control
                                  type="date"
                                  size="sm"
                                  value={ticketRangeEnd}
                                  onChange={(e) => setTicketRangeEnd(e.target.value)}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <div className="d-grid">
                            <Button
                              variant={ticketRangeStart || ticketRangeEnd ? "primary" : "outline-primary"}
                              onClick={() => {
                                const params = new URLSearchParams();
                                if (ticketRangeStart) params.set("start_date", ticketRangeStart);
                                if (ticketRangeEnd) params.set("end_date", ticketRangeEnd);
                                const hasRange = params.size > 0;
                                downloadReport(report.endpoint, report.name, {
                                  query: hasRange ? `?${params.toString()}` : "",
                                  nameSuffix: hasRange
                                    ? `${ticketRangeStart || "open"}_to_${ticketRangeEnd || "open"}`
                                    : undefined,
                                });
                              }}
                            >
                              {ticketRangeStart || ticketRangeEnd
                                ? `Download for ${ticketRangeStart || "earliest"} – ${ticketRangeEnd || "latest"}`
                                : `Download for ${schoolYearName}`}
                            </Button>
                          </div>
                          <Form.Text className="text-muted">
                            Dates filter by performance date and ignore the school-year picker
                            (they can span years). Leave blank to download the selected school year.
                          </Form.Text>
                        </div>
                      ) : (
                        <Button
                          key={report.endpoint}
                          variant="outline-primary"
                          onClick={() => downloadReport(report.endpoint, report.name)}
                        >
                          {report.label}
                        </Button>
                      )
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
}

export default Reporting;
